import { useState, useEffect } from 'react';
import type { EmailOtpType } from '@supabase/supabase-js';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, AlertCircle, Building2 } from 'lucide-react';

interface LoginFormProps {
  onSwitchToSignup: () => void;
  onForgotPassword: () => void;
  firmInvite?: { firm_name: string; invited_email: string } | null;
  notice?: { type: 'success' | 'error'; message: string; otpType: EmailOtpType } | null;
}

export function LoginForm({ onSwitchToSignup, onForgotPassword, firmInvite, notice }: LoginFormProps) {
  const { signIn, resendConfirmation } = useAuth();
  const [email, setEmail] = useState(firmInvite?.invited_email ?? '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // `notice` arrives async — App.tsx's token_hash confirm call hasn't resolved
  // yet on first render — so this can't be a useState initializer; it has to
  // react to the prop landing after mount. Only a stale *signup* link offers
  // this resend action — it calls resendConfirmation, which sends a signup
  // confirmation, not a password reset. A stale recovery link's notice text
  // points at "Forgot password?" below instead.
  useEffect(() => {
    if (notice?.type === 'error' && notice.otpType === 'signup') setShowResend(true);
  }, [notice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setLoading(true);

    const { error: signInError } = await signIn(email, password);

    if (signInError) {
      const msg = signInError.message.toLowerCase();
      if (msg.includes('email not confirmed') || msg.includes('not confirmed') || msg.includes('confirm')) {
        setError('Please verify your email before signing in. Check your inbox — and your spam or junk folder — for a confirmation link from TrueLoad.');
        setShowResend(true);
        setLoading(false);
        return;
      }
      if (msg.includes('ban') || msg.includes('suspended') || msg.includes('locked')) {
        try {
          const resp = await fetch('/.netlify/functions/check-account-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await resp.json();
          if (data.status === 'deleted') {
            setError('This account has been deactivated. Please contact support at support@ethermsolutions.com if you believe this is an error.');
          } else if (data.status === 'locked') {
            setError('Your account has been temporarily suspended. Please contact support at support@ethermsolutions.com to restore access.');
          } else {
            setError(signInError.message);
          }
        } catch {
          setError(signInError.message);
        }
      } else {
        setError(signInError.message);
      }
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setResendMessage('Enter your email address above first.');
      return;
    }
    setResendStatus('sending');
    setResendMessage('');
    const { error: resendError } = await resendConfirmation(email);
    if (resendError) {
      setResendStatus('error');
      const m = resendError.message.toLowerCase();
      if (m.includes('rate') || m.includes('seconds') || m.includes('too many') || m.includes('limit')) {
        setResendMessage('Please wait a moment before requesting another email.');
      } else if (m.includes('already') || m.includes('confirmed')) {
        setResendMessage('This email is already confirmed — try signing in again.');
      } else {
        setResendMessage('Could not resend right now. Please try again in a minute.');
      }
    } else {
      setResendStatus('sent');
      setResendMessage('Sent! Check your inbox (and spam folder) again.');
      setResendCooldown(60);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 px-4">
      <div className="max-w-md w-full">
        {firmInvite && (
          <div className="mb-4 bg-blue-800 text-white rounded-xl px-6 py-4 shadow-lg">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 flex-shrink-0" />
              <span className="font-semibold text-sm">You've been invited to join {firmInvite.firm_name}</span>
            </div>
            <p className="text-blue-200 text-xs">Sign in to accept your invitation, or create a new account below.</p>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
              <img
                src="/trueload_icon_512.png"
                alt="TrueLoad"
                width={64}
                height={64}
                className="w-16 h-16 rounded-2xl"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Welcome Back</h2>
            <p className="text-gray-600 mt-2">Sign in to your TrueLoad account</p>
          </div>

          {notice && (
            <div
              data-testid="login-notice"
              className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${
                notice.type === 'success'
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-amber-50 border border-amber-200'
              }`}
            >
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${notice.type === 'success' ? 'text-green-600' : 'text-amber-600'}`} />
              <p className={`text-sm ${notice.type === 'success' ? 'text-green-800' : 'text-amber-800'}`}>{notice.message}</p>
            </div>
          )}

          {error && (
            <div data-testid="login-error" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {showResend && (
            <div className="mb-4 -mt-1">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendStatus === 'sending' || resendCooldown > 0}
                data-testid="login-resend-confirmation"
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {resendStatus === 'sending'
                  ? 'Sending…'
                  : resendCooldown > 0
                    ? `Resend available in ${resendCooldown}s`
                    : 'Resend confirmation email'}
              </button>
              {resendMessage && (
                <p className={`text-sm mt-2 ${resendStatus === 'error' ? 'text-red-600' : 'text-green-700'}`}>{resendMessage}</p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { if (!firmInvite) setEmail(e.target.value); }}
                  readOnly={!!firmInvite}
                  required
                  data-testid="login-email"
                  className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${firmInvite ? 'bg-gray-50' : ''}`}
                  placeholder="you@example.com"
                />
                {firmInvite && (
                  <p className="text-xs text-gray-500 mt-1">This email was used for your invitation and cannot be changed.</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onForgotPassword}
                data-testid="login-forgot-password"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={onSwitchToSignup}
                data-testid="login-switch-signup"
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
